import { useEffect, useState, useCallback, useRef } from "react";
import { IGetIssuesListData } from "../types/rules";
import {
  getPaginatedRuleResearchIssuesDetail,
  getPaginatedGenericResearchIssuesDetail,
  getPaginatedValidationIssuesDetail,
  getPaginatedExecutionIssuesDetail,
} from "../services/IssueManagementService";

// Type definitions for better type safety
type PageType = "execution" | "validation" | "ruleResearch" | "genericResearch";

// Service function mapping for cleaner code
const issueServiceMap = {
  execution: getPaginatedExecutionIssuesDetail,
  validation: getPaginatedValidationIssuesDetail,
  ruleResearch: getPaginatedRuleResearchIssuesDetail,
  genericResearch: getPaginatedGenericResearchIssuesDetail,
} as const;

interface FetchIssuesProps {
  searchFilterData: any;
  setIsLoading: (loading: boolean) => void;
  currentPageType: any;
  page: number;
  pSize: number;
}

const useFetchAllPaginatedIssues = ({
  searchFilterData,
  setIsLoading,
  currentPageType,
  page,
  pSize,
}: FetchIssuesProps) => {
  const [issuesList, setIssuesList] = useState<any[]>([]);

  // Use ref to always have latest searchFilterData without causing re-renders
  const searchFilterDataRef = useRef(searchFilterData);
  searchFilterDataRef.current = searchFilterData;

  // Strip view_type from serialization — it is a frontend-only tab-switcher
  // state and must not trigger re-fetches or be sent to the backend.
  const { view_type: _vt, ...filterDataForSerialization } =
    searchFilterData || {};
  // Serialize the remaining filter data to a stable string for dependency comparison
  // This prevents re-fetching when the object reference changes but values are the same
  const serializedFilterData = JSON.stringify(filterDataForSerialization);

  // Helper function to get the appropriate service function
  const getIssueService = useCallback((pageType: PageType) => {
    const service = issueServiceMap[pageType];
    if (!service) {
      console.warn(`Unknown page type: ${pageType}`);
      return null;
    }
    return service;
  }, []);

  useEffect(() => {
    const currentFilterData = searchFilterDataRef.current;
    if (Object.keys(currentFilterData).length === 0) {
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Strip view_type from payload — it is a frontend-only state and
        // must not be sent to the backend APIs.
        const { date, view_type, ...filteredSearchData } = currentFilterData;

        // Get the appropriate service function based on page type
        const serviceFunction = getIssueService(currentPageType);

        if (!serviceFunction) {
          console.error(
            `No service function found for page type: ${currentPageType}`
          );
          if (!cancelled) setIssuesList([]);
          return;
        }

        // Call the service function with consistent parameters
        const result = await serviceFunction({
          payload: filteredSearchData,
          page,
          pSize,
        });

        if (!cancelled) setIssuesList(result || []);
      } catch (error) {
        console.error(`Error fetching issues for ${currentPageType}:`, error);
        if (!cancelled) setIssuesList([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serializedFilterData, // Stable string comparison instead of object reference
    currentPageType,
    page,
    pSize,
    getIssueService,
    setIsLoading,
  ]);

  return issuesList;
};

export default useFetchAllPaginatedIssues;
